import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaGithub, FaLinkedin, FaInstagram, FaEnvelope } from 'react-icons/fa';

const Hero = () => {
  const socialLinks = [
    { icon: <FaGithub />, url: "https://github.com/Rishi9322", label: "GitHub" },
    { icon: <FaLinkedin />, url: "https://www.linkedin.com/in/rishi-poddar/", label: "LinkedIn" },
    { icon: <FaInstagram />, url: "https://www.instagram.com/rishipoddar05/", label: "Instagram" },
    { icon: <FaEnvelope />, url: "mailto:rishipoddarr@gmail.com", label: "Email" }
  ];

  return (
    <HeroSection id="hero">
      <ContentWrapper>
        <TextContent>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Greeting>Hi, my name is</Greeting>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Name>Rishi Poddar</Name>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Headline>BSc IT Student & Web Developer</Headline>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Description>
              I'm currently pursuing my BSc in Information Technology at Thakur College of Science and Commerce.
              Passionate about web development and creating user-friendly applications using modern technologies.
            </Description>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <ButtonContainer>
              <CTAButton href="/projects">Explore My Projects</CTAButton>
              <CTAButton href="/about" $secondary>More About Me</CTAButton>
            </ButtonContainer>
          </motion.div>

          <SocialLinks>
            {socialLinks.map((link, index) => (
              <SocialLink 
                key={link.label}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                {link.icon}
                <span className="sr-only">{link.label}</span>
              </SocialLink>
            ))}
          </SocialLinks>
        </TextContent>

        <ImageSection
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <ProfileImage 
            src="https://iili.io/3nvZZeS.png/400/400" 
            alt="Rishi Poddar"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
          />
        </ImageSection>
      </ContentWrapper>
    </HeroSection>
  );
};

const HeroSection = styled.section`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 100px 25px 0; // Changed padding to ensure content starts below navbar
  margin-top: 0; // Removed negative margin
`;

const ContentWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1000px;
  width: 100%;
  gap: 4rem;
  padding-top: 50px; // Added padding to push content down

  @media (max-width: 768px) {
    flex-direction: column-reverse;
    gap: 2rem;
    padding-top: 30px; // Adjusted for mobile
  }
`;

const TextContent = styled.div`
  flex: 1;
`;

const ImageSection = styled(motion.div)`
  flex: 0.8;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ProfileImage = styled(motion.img)`
  width: 300px;
  height: 300px;
  border-radius: 15px;
  object-fit: cover;
  border: 2px solid var(--accent);
  filter: brightness(0.9);
  transition: all 0.3s ease;

  &:hover {
    filter: brightness(1);
    transform: scale(1.02);
  }

  @media (max-width: 768px) {
    width: 250px;
    height: 250px;
  }
`;

const Greeting = styled.h3`
  color: var(--accent);
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 16px;
  font-weight: 400;
  margin-bottom: 40px;  // Increased from 20px to 40px
  
  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 30px;  // Added responsive margin
  }
`;

const Name = styled.h1`
  font-size: 80px;
  margin: 0 0 20px 0;  // Increased bottom margin from 10px to 20px
  color: var(--text-primary);
  padding-top: 20px;   // Added top padding
  
  @media (max-width: 768px) {
    font-size: 50px;
    margin: 0 0 15px 0;
    padding-top: 15px;
  }
  
  @media (max-width: 480px) {
    font-size: 40px;
    margin: 0 0 10px 0;
    padding-top: 10px;
  }
`;

const Headline = styled.h2`
  font-size: 70px;
  margin: 0 0 20px 0;
  color: var(--text-secondary);
  line-height: 1.1;
  
  @media (max-width: 768px) {
    font-size: 40px;
  }
  
  @media (max-width: 480px) {
    font-size: 30px;
  }
`;

const Description = styled.p`
  max-width: 540px;
  margin-bottom: 40px;
  color: var(--text-secondary);
  font-size: 18px;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const CTAButton = styled.a`
  display: inline-block;
  text-align: center;
  padding: 1.25rem 1.75rem;
  background-color: ${props => props.$secondary ? 'rgba(100, 255, 218, 0.1)' : 'transparent'};
  color: var(--accent);
  border: 1px solid var(--accent);
  border-radius: var(--border-radius);
  font-size: 14px;
  font-family: 'SF Mono', 'Fira Code', monospace;
  line-height: 1;
  text-decoration: none;
  cursor: pointer;
  transition: var(--transition);
  width: fit-content;
  
  &:hover {
    background-color: rgba(100, 255, 218, 0.1);
    transform: translateY(-3px);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-top: 2rem;
`;

const SocialLink = styled(motion.a)`
  color: var(--text-secondary);
  font-size: 1.5rem;
  transition: var(--transition);

  &:hover {
    color: var(--accent);
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
  }
`;

export default Hero;